import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DomainSelect from './DomainSelect';
import Tags from './TagsReact';
import DateRange from './DateRange';
import { CSSTransition } from 'react-transition-group';
import { useForm, Controller } from 'react-hook-form';
import moment from 'moment';
import { Common } from '../../utils/common';
import Select from 'react-select';
import EmployeeSelect from './EmployeeSelect';

function ReportFilters({
  filters,
  setFilters,
  open,
  setOpen,
  defaultFilterState,
  controlledDateRange,
  setControlledDateRange,
  selectInputs,
  datePickerMode = 'daterange',
}) {
  const [selectedDomainsIsOpen, setSelectedDomainsIsOpen] = useState(true);

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    register,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: filters,
  });

  // Keeps form input values consistent with current filters
  useEffect(() => {
    reset({ ...filters, domains: getValues('domains') });
  }, [filters, reset, getValues]);

  const showInactive = watch('show_inactive');
  const domains = watch('domains');

  /** @type {number[]} */
  const groupIds = [];
  /** @type {number[]} */
  const locationIds = [];
  domains?.forEach((item) => {
    if (item.className.includes('filter-group')) {
      groupIds.push(item.value);
    } else if (item.className.includes('filter-location')) {
      locationIds.push(item.value);
    }
  });

  function applyFilters(formData) {
    // formData is all the values from the inputs registered with react-hook-form
    let filters = { ...formData };

    // The DateRange component can't be registered by react-hook-form, so we have to
    // add it's values to the filters object here
    filters.start_date = controlledDateRange.start.format('YYYYMMDD');
    filters.end_date = controlledDateRange.end.format('YYYYMMDD');

    setFilters(filters);
    setOpen(false);
  }

  function clearAllFilters() {
    // Resets the form values to their default state. The DomainSelect component can't be
    // programatically reset, so we leave it unchanged. The show_inactive value controls what
    // is visible in the domain select, so we leave that unchanged as well.
    reset({
      ...defaultFilterState,
      domains: getValues('domains'),
      show_inactive: getValues('show_inactive'),
    });
    // Cancels unsaved changes to DatePicker UI
    setControlledDateRange({
      start: moment(filters.start_date, 'YYYYMMDD'),
      end: moment(filters.end_date, 'YYYYMMDD'),
    });
    setFilters((prevFilters) => ({
      ...defaultFilterState,
      // These properties shouldn't be set back to the default state when the user clears the filters
      start_date: prevFilters.start_date,
      end_date: prevFilters.end_date,
      domains: prevFilters.domains,
      show_inactive: prevFilters.show_inactive,
    }));
    setOpen(false);
  }

  return (
    <>
      <CSSTransition in={open} timeout={300} classNames='backdrop' appear>
        <div onClick={() => setOpen(false)}></div>
      </CSSTransition>
      <div className={`sidedrawer ${open ? 'open' : ''}`}>
        <div
          onClick={() => {
            setOpen((prevOpen) => !prevOpen);
          }}
          className='filter-button'
          role='button'
        >
          <svg
            style={{ height: '20px', width: '20px' }}
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 12 12'
          >
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d='M11.5 0c.273 0 .5.272.5.6v1.2c0 .328-.227.6-.5.6H.5c-.273 0-.5-.272-.5-.6V.6C0 .272.227 0 .5 0h11ZM9.907 5.4c0-.328-.148-.6-.326-.6H2.42c-.179 0-.326.272-.326.6v1.2c0 .328.147.6.326.6H9.58c.178 0 .326-.272.326-.6V5.4ZM8.51 10.2c0-.328-.095-.6-.209-.6H3.698c-.115 0-.21.272-.21.6v1.2c0 .328.095.6.21.6h4.604c.114 0 .21-.272.21-.6v-1.2Z'
              fill='#434A54'
            />
          </svg>
        </div>
        <div className='sidedrawer-content'>
          <div style={{ paddingLeft: '30px', paddingRight: '30px' }}>
            <div className='sidedrawer-header'>
              <h2>
                <svg
                  style={{
                    height: '10px',
                    width: '10px',
                    marginRight: '5px',
                    display: 'inline-block',
                  }}
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  viewBox='0 0 12 12'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M11.5 0c.273 0 .5.272.5.6v1.2c0 .328-.227.6-.5.6H.5c-.273 0-.5-.272-.5-.6V.6C0 .272.227 0 .5 0h11ZM9.907 5.4c0-.328-.148-.6-.326-.6H2.42c-.179 0-.326.272-.326.6v1.2c0 .328.147.6.326.6H9.58c.178 0 .326-.272.326-.6V5.4ZM8.51 10.2c0-.328-.095-.6-.209-.6H3.698c-.115 0-.21.272-.21.6v1.2c0 .328.095.6.21.6h4.604c.114 0 .21-.272.21-.6v-1.2Z'
                    fill='#434A54'
                  />
                </svg>
                Filters:
              </h2>
              <button
                onClick={() => setOpen(false)}
                type='button'
                className='close'
                aria-label='Close'
              >
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
          </div>
          <form onSubmit={handleSubmit(applyFilters)} className='sidedrawer-form'>
            <div className='submit-buttons-container col-xs-12'>
              <button onClick={clearAllFilters} type='button' className='btn btn-default'>
                Clear
              </button>
              <button style={{ marginLeft: '1rem' }} type='submit' className='btn btn-primary'>
                Apply
              </button>
            </div>
            {datePickerMode === 'none' ? null : (
              <div className='col-xs-12 form-group'>
                <label>Date Range</label>
                <div>
                  <DateRange
                    filters={filters}
                    setFilters={setFilters}
                    controlledDateRange={controlledDateRange}
                    setControlledDateRange={setControlledDateRange}
                    isControlled
                    datePickerMode={datePickerMode}
                  />
                </div>
              </div>
            )}
            {!Common.in_crm_mode() && (
              <div className={`form-group col-xs-12 ${errors.domains ? 'has-error' : ''}`}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                  <i
                    role='button'
                    onClick={() => {
                      setSelectedDomainsIsOpen((prevState) => !prevState);
                    }}
                    className={`fa groups-and-locations-toggle-icon ${
                      selectedDomainsIsOpen ? 'fa-caret-down' : 'fa-caret-right'
                    }`}
                  />
                  <label style={{ marginBottom: 0 }} className='control-label'>
                    Groups and Locations
                  </label>
                  <input
                    id='show_inactive'
                    style={{ margin: '0px 4px 0px 16px' }}
                    type='checkbox'
                    {...register('show_inactive')}
                  />
                  <label htmlFor='show_inactive' style={{ marginBottom: 0 }}>
                    Show inactive groups and locations
                  </label>
                </div>
                <div className={`${selectedDomainsIsOpen ? '' : 'hidden'}`}>
                  <Controller
                    key={showInactive}
                    name='domains'
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <DomainSelect
                        field={field}
                        fieldState={fieldState}
                        filters={filters}
                        showInactive={showInactive}
                      />
                    )}
                  />
                </div>
                {errors.domains && (
                  <div className='help-block'>Must select a group or location</div>
                )}
              </div>
            )}
            {selectInputs &&
              Object.keys(selectInputs).map((filter) => {
                return (
                  <div key={filter} className='form-group col-xs-12 col-sm-6'>
                    <label htmlFor={filter}>{selectInputs[filter].labelText}</label>
                    <Controller
                      name={filter}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          id={filter}
                          placeholder={`Select ${selectInputs[filter].labelText}`}
                          options={selectInputs[filter].options}
                          isMulti
                        />
                      )}
                    />
                  </div>
                );
              })}
            {defaultFilterState.tag_ids && !Common.in_crm_mode() && (
              <div className='form-group col-xs-12 col-sm-6'>
                <label htmlFor='tag_ids'>Tags</label>
                <Controller
                  name='tag_ids'
                  control={control}
                  render={({ field }) => <Tags getValues={getValues} field={field} />}
                />
              </div>
            )}
            {defaultFilterState.employees && !Common.in_crm_mode() && (
              <div className='form-group col-xs-12 col-sm-6'>
                <label htmlFor='employees'>Employees</label>
                <Controller
                  name='employees'
                  control={control}
                  render={({ field }) => (
                    <EmployeeSelect
                      onChange={field.onChange}
                      value={field.value}
                      locationIds={locationIds}
                      groupIds={groupIds}
                      showInactive={showInactive}
                      isMulti={true}
                    />
                  )}
                />
              </div>
            )}
            {defaultFilterState.hasOwnProperty('ds_appt_set') &&
              defaultFilterState.hasOwnProperty('ds_alert_sent') && (
                <>
                  <div className='flex col-xs-12 col-sm-6 form-group'>
                    <div className='col-xs-7 no-gutters'>
                      <label>Only by appointments</label>
                    </div>
                    <div className='col-xs-5 no-gutters'>
                      <input
                        style={{ marginLeft: '5px' }}
                        {...register('ds_appt_set')}
                        type='checkbox'
                        id='ds_appt_set'
                      />
                    </div>
                  </div>
                  <div className='flex col-xs-12 col-sm-6 form-group'>
                    <div className='col-xs-7 no-gutters'>
                      <label>Only with alerts</label>
                    </div>
                    <div className='col-xs-5 no-gutters'>
                      <input
                        style={{ marginLeft: '5px' }}
                        {...register('ds_alert_sent')}
                        type='checkbox'
                        id='ds_alert_sent'
                      />
                    </div>
                  </div>
                </>
              )}
            <div className='submit-buttons-container col-xs-12'>
              <button onClick={clearAllFilters} type='button' className='btn btn-default'>
                Clear
              </button>
              <button style={{ marginLeft: '1rem' }} type='submit' className='btn btn-primary'>
                Apply
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

ReportFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  defaultFilterState: PropTypes.object.isRequired,
  controlledDateRange: PropTypes.shape({
    start: PropTypes.object,
    end: PropTypes.object,
  }).isRequired,
  setControlledDateRange: PropTypes.func.isRequired,
  selectInputs: PropTypes.object,
  datePickerMode: PropTypes.string,
};

export default ReportFilters;
